/**
 * The help bubble: a small "?" in the corner that says how to use the page it
 * is on, and lists the other jobs the app does.
 *
 * It exists so the steps can be read beside the page while they are carried
 * out, which the Help page (a separate screen) cannot do. That decides how it
 * behaves:
 *
 *   - It stays open while you click around the page and from page to page, and
 *     closes only when asked to (the bubble again, the ×, or Escape).
 *   - It follows the page. If a job was picked from the list instead, it keeps
 *     showing that job until closed, because jobs cross pages.
 *   - It is one small control in the same corner of every screen, and nothing
 *     else on the page moves to make room for it.
 *
 * Desktop widths only (styles.css): on a phone the corner belongs to the tab
 * bar, and Help is in the menu.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TOPICS, topicForPath, type HelpTopic } from './helpTopics.js';

/** A picked topic id, the list of how-tos, or null for "whatever page this is". */
type View = string | 'list' | null;

export function HelpBubble() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>(null);
  const panel = useRef<HTMLDivElement>(null);
  const bubble = useRef<HTMLButtonElement>(null);

  const close = () => {
    setOpen(false);
    setView(null);
  };

  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      close();
      bubble.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const pageTopic = find(topicForPath(pathname));
  // A page with no topic of its own (Help, an unknown address) shows the list.
  const topic = view === 'list' ? null : (find(view) ?? pageTopic);
  const picked = topic !== null && view !== null && topic.id !== pageTopic?.id;

  return (
    <div className="help">
      <button
        ref={bubble}
        className="help-bubble"
        aria-label="How this page works"
        title="How this page works"
        aria-expanded={open}
        aria-controls="help-panel"
        onClick={() => (open ? close() : setOpen(true))}
      >
        ?
      </button>

      {open ? (
        <div className="help-panel" id="help-panel" role="region" aria-label="Help" tabIndex={-1} ref={panel}>
          <div className="help-head">
            <span className="help-kicker">{topic ? (picked ? 'How to' : 'How this page works') : 'Help'}</span>
            <button className="help-close" aria-label="Close help" onClick={close}>
              ×
            </button>
          </div>

          {/* Only the middle scrolls, so the way out is always in view. */}
          <div className="help-body">
            {topic ? (
              <Steps topic={topic} here={topic.id === pageTopic?.id} />
            ) : (
              <>
                <h2>How do I…</h2>
                <div className="help-list">
                  {TOPICS.filter((t) => t.listed).map((t) => (
                    <button key={t.id} onClick={() => setView(t.id)}>
                      {t.title}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="help-foot">
            {topic ? (
              <button className="quiet" onClick={() => setView('list')}>
                All how-tos
              </button>
            ) : null}
            {pageTopic && (picked || !topic) ? (
              <button className="quiet" onClick={() => setView(null)}>
                This page
              </button>
            ) : null}
            {pathname === '/help' ? null : (
              <Link className="btn quiet" to="/help">
                Full guide
              </Link>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function find(id: string | null): HelpTopic | null {
  return TOPICS.find((t) => t.id === id) ?? null;
}

/** `here`: this is the page the job is done on, so "how to get there" is noise. */
function Steps({ topic, here }: { topic: HelpTopic; here: boolean }) {
  const List = topic.ordered === false ? 'ul' : 'ol';
  const steps = topic.start && !here ? [topic.start, ...topic.steps] : topic.steps;
  return (
    <>
      <h2>{topic.title}</h2>
      <List>
        {steps.map((step) => (
          <li key={step}>{rich(step)}</li>
        ))}
      </List>
      {topic.notes?.map((note) => (
        <p className="help-note" key={note}>
          {rich(note)}
        </p>
      ))}
    </>
  );
}

/** **label** and [words](/path), as described in helpTopics.ts. */
function rich(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  for (let m = pattern.exec(text); m; m = pattern.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      m[1] !== undefined ? (
        <strong key={m.index}>{m[1]}</strong>
      ) : (
        <Link key={m.index} to={m[3]!}>
          {m[2]}
        </Link>
      ),
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
